import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import {
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  TotpMultiFactorGenerator,
  type MultiFactorInfo,
  type TotpSecret,
  type User,
} from "firebase/auth";
import { paths, twoStepDueAt, twoStepRequired } from "@clevis/shared-types";
import { auth, db } from "./firebase";
import { useAuth } from "../hooks/useAuth";

// Two-step sign-in in the app: setting it up (an authenticator app, or a
// texted code as the backup), and knowing who must have it. Signing in with
// it is TwoStepChallenge; Firebase enforces it at every sign-in once set up.

const CHANGED = "clevis:twostep";
/** Set by the sign-in page so the setup step is offered once per sign-in, not on every page. */
export const FRESH_SIGN_IN = "clevis.freshSignIn";
export const markFreshSignIn = () => {
  try {
    sessionStorage.setItem(FRESH_SIGN_IN, "1");
  } catch {
    /* private window */
  }
};

export const factorsOf = (user: User | null): MultiFactorInfo[] => (user ? multiFactor(user).enrolledFactors : []);
export const factorLabel = (f: MultiFactorInfo) =>
  f.factorId === TotpMultiFactorGenerator.FACTOR_ID ? f.displayName || "Authenticator app" : `Text to ${(f as MultiFactorInfo & { phoneNumber?: string }).phoneNumber ?? "your phone"}`;

/** Firebase's error code, if any (auth/requires-recent-login, auth/invalid-verification-code…). */
export const authCode = (err: unknown) => (err as { code?: string }).code ?? "";

export function twoStepMessage(err: unknown): string {
  switch (authCode(err)) {
    case "auth/invalid-verification-code":
    case "auth/invalid-totp-code":
      return "That code isn't right. Codes change every 30 seconds; try the one showing now.";
    case "auth/code-expired":
      return "That code ran out. Send a new one.";
    case "auth/operation-not-allowed":
      return "Two-step sign-in isn't switched on for Clevis yet. Try again later.";
    case "auth/too-many-requests":
      return "Too many tries. Wait a few minutes.";
    case "auth/invalid-phone-number":
      return "Enter the cell number with area code.";
    case "auth/unverified-email":
      return "Confirm your email address first (check your inbox), then set this up.";
    default:
      return (err as Error).message.replace("Firebase: ", "");
  }
}

async function changed(user: User) {
  await user.reload();
  await user.getIdToken(true);
  window.dispatchEvent(new Event(CHANGED));
}

/** Step 1 for an authenticator app: the secret to scan (or type in). */
export async function startAppSetup(user: User): Promise<{ secret: TotpSecret; uri: string; key: string }> {
  const session = await multiFactor(user).getSession();
  const secret = await TotpMultiFactorGenerator.generateSecret(session);
  return { secret, uri: secret.generateQrCodeUrl(user.email ?? "Clevis", "Clevis"), key: secret.secretKey };
}
export async function finishAppSetup(user: User, secret: TotpSecret, code: string): Promise<void> {
  await multiFactor(user).enroll(TotpMultiFactorGenerator.assertionForEnrollment(secret, code), "Authenticator app");
  await changed(user);
}

/** An invisible "are you a person" check Firebase needs before it texts a code. */
export function newVerifier(containerId: string): RecaptchaVerifier {
  return new RecaptchaVerifier(auth, containerId, { size: "invisible" });
}
export async function startTextSetup(user: User, phone: string, verifier: RecaptchaVerifier): Promise<string> {
  const session = await multiFactor(user).getSession();
  return new PhoneAuthProvider(auth).verifyPhoneNumber({ phoneNumber: phone, session }, verifier);
}
export async function finishTextSetup(user: User, verificationId: string, code: string): Promise<void> {
  await multiFactor(user).enroll(PhoneMultiFactorGenerator.assertion(PhoneAuthProvider.credential(verificationId, code)), "Text message");
  await changed(user);
}
export async function removeFactor(user: User, f: MultiFactorInfo): Promise<void> {
  await multiFactor(user).unenroll(f);
  await changed(user);
}

/** "+1" for a US number typed any way; leaves an international one alone. */
export function e164(raw: string): string {
  const d = raw.replace(/\D/g, "");
  if (raw.trim().startsWith("+")) return `+${d}`;
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d.startsWith("1")) return `+${d}`;
  return raw.trim();
}

/** This person's two-step: what they have, whether they must, and by when. */
export function useTwoStep(): { factors: MultiFactorInfo[]; required: boolean; dueAt: number; loaded: boolean; refresh: () => void } {
  const { user, role, superAdmin } = useAuth();
  const [factors, setFactors] = useState<MultiFactorInfo[]>(() => factorsOf(user));
  const [roleSince, setRoleSince] = useState<number | null>(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    setFactors(factorsOf(user));
    const on = () => setFactors(factorsOf(auth.currentUser));
    window.addEventListener(CHANGED, on);
    return () => window.removeEventListener(CHANGED, on);
  }, [user]);
  useEffect(() => {
    if (!user) return;
    return onSnapshot(
      doc(db, paths.user(user.uid)),
      (s) => {
        setRoleSince((s.get("roleSince") as number | undefined) ?? null);
        setLoaded(true);
      },
      () => setLoaded(true),
    );
  }, [user]);
  return {
    factors,
    required: twoStepRequired(role, superAdmin),
    dueAt: twoStepDueAt(roleSince),
    loaded,
    refresh: () => setFactors(factorsOf(auth.currentUser)),
  };
}
